import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import { globalStyles, noop } from "@client/shared/constants";
import { decodeToken, getToken } from "@client/utils";
import style from "./style";
import MicIcon from "@mui/icons-material/Mic";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import Recorder from "./recorder";
import CallToRecord from "./call-to-record";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const RADIO_LIST = [
  { label: "Record a audio from your computer.", value: 1, type: "UPLOAD" },
  { label: "Have RingCentral call me to record.", value: 2, type: "UPLOAD" },
  { label: "Select a previously used audio file.", value: 3, type: "ACCESS" },
  {
    label: "Upload an audio file from your computer.",
    value: 4,
    type: "UPLOAD",
  },
];

const defaultState = {
  audioSource: null,
  isDraggingOnDropZone: false,
};

const AudioRecordView = ({
  recordingType = 1,
  voicemailList = [],
  uploadedVoicemail = null,
  selectedVoicemail = {},
  recordedVoicemail = null,
  recordedVoicemailTitle = "",
  callToRecordVoicemail = null,
  callToRecordVoicemailNumber = null,
  callToRecordVoicemailTitle = "",
  SUPPORTED_FILE_FORMATS = [],
  roleAccess = {},
  phoneNumberList = [],
  countryList = [],
  handleChange = noop,
  handleFileChange = noop,
  handleJwksResponse = noop,
}) => {
  const [state, setState] = useState(defaultState);
  const hasPermission = (recordingType) => {
    const permission = {
      1: roleAccess.uploadMedia,
      2: roleAccess.uploadMedia,
      3: roleAccess.accessMedia,
      4: roleAccess.uploadMedia,
    };
    return permission[recordingType];
  };

  const updateAudioSource = () => {
    const token = decodeToken(getToken());
    let audioSource = null;
    switch (+recordingType) {
      case 1:
        audioSource =
          recordedVoicemail &&
          (window.URL || window.webkitURL).createObjectURL(recordedVoicemail);
        break;
      case 2:
        audioSource =
          callToRecordVoicemail?.contentUri &&
          `${callToRecordVoicemail?.contentUri}?access_token=${token.rc_access_token}`;
        // audioSource =
        //   callToRecordVoicemail?.uri &&
        //   `${callToRecordVoicemail?.uri}?access_token=${token.rc_access_token}`;
        break;
      case 3:
        audioSource =
          selectedVoicemail?.contentUri &&
          `${selectedVoicemail?.contentUri}?access_token=${token.rc_access_token}`;
        break;
      case 4:
        audioSource =
          uploadedVoicemail &&
          (window.URL || window.webkitURL).createObjectURL(uploadedVoicemail);
        break;
    }

    setState((prevState) => ({
      ...prevState,
      audioSource,
    }));
  };

  useEffect(() => {
    updateAudioSource();
  }, [
    recordingType,
    uploadedVoicemail,
    selectedVoicemail,
    recordedVoicemail,
    callToRecordVoicemail,
  ]);

  return (
    <>
      <FormControl>
        <RadioGroup
          name="recordingType"
          value={recordingType}
          onChange={(evt) => handleChange(evt, "radioGroup")}
        >
          {RADIO_LIST.filter((item) => {
            return (
              (item.type === "UPLOAD" && roleAccess?.uploadMedia) ||
              (item.type === "ACCESS" && roleAccess?.accessMedia)
            );
          }).map((radio, index) => (
            <FormControlLabel
              key={index}
              value={radio.value}
              label={<Typography variant="body2">{radio.label}</Typography>}
              control={<Radio size="small" />}
            />
          ))}
        </RadioGroup>
      </FormControl>
      {!roleAccess?.uploadMedia && !roleAccess?.accessMedia && (
        <Box
          sx={{
            ...globalStyles.display.flex,
            ...globalStyles.flex.direction.column,
            ...globalStyles.flex.align.center,
          }}
        >
          <Box sx={{ mt: 2 }}>
            <img height={116} alt="Alert Icon" src="/assets/error-icon.svg" />
          </Box>
          {/* <WarningRoundedIcon
            fontSize="large"
            color="error"
            sx={style.infoBtn}
          /> */}
          <Typography variant="h4" sx={style.heading}>
            Access Denied
          </Typography>
          <Typography variant="body1">
            Please contact your RingCentral Account Administrator for access.
          </Typography>
        </Box>
      )}
      {+recordingType === 1 && hasPermission(+recordingType) && (
        <Recorder
          recordedVoicemailTitle={recordedVoicemailTitle}
          handleChange={handleChange}
        />
      )}
      {+recordingType === 2 && hasPermission(+recordingType) && (
        <CallToRecord
          callToRecordVoicemail={callToRecordVoicemail}
          callToRecordVoicemailNumber={callToRecordVoicemailNumber}
          callToRecordVoicemailTitle={callToRecordVoicemailTitle}
          phoneNumberList={phoneNumberList}
          countryList={countryList}
          handleChange={handleChange}
          handleJwksResponse={handleJwksResponse}
          voicemailList={voicemailList}
        />
      )}
      {+recordingType === 3 && hasPermission(+recordingType) && (
        <Box
          sx={{
            mt: 4,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              ...globalStyles.text.weight.bold,
            }}
          >
            Search Existing Recordings
          </Typography>
          <Box
            sx={{
              ...globalStyles.display.flex,
              ...globalStyles.flex.justify.between,
              mt: 2,
            }}
          >
            <Autocomplete
              fullWidth
              size="small"
              value={selectedVoicemail}
              options={voicemailList}
              getOptionLabel={(option) => option.filename || ""}
              onChange={(evt, value) => {
                handleChange(
                  { name: "selectedVoicemail", value: value || {} },
                  "autocomplete"
                );
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Start typing the name..." />
              )}
            />
          </Box>
        </Box>
      )}
      {+recordingType === 4 && hasPermission(+recordingType) && (
        <Box
          sx={{
            mt: 4,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              ...globalStyles.text.weight.bold,
            }}
          >
            Upload from your computer
          </Typography>
          <Box
            sx={{
              ...globalStyles.display.flex,
              ...globalStyles.flex.justify.between,
              ...globalStyles.flex.align.center,
              mt: 2,
            }}
          >
            <Box
              sx={{
                px: 4,
                py: 1,
                mr: 1,
                ...style.dragWrapper,
                ...(state.isDraggingOnDropZone ? style.dragging : {}),
                ...globalStyles.width["75"],
                ...globalStyles.display.flex,
                ...globalStyles.flex.justify.center,
              }}
              onDragEnter={() => {
                setState(prevState => ({
                  ...prevState,
                  isDraggingOnDropZone: true,
                }));
              }}
              onDragLeave={() => {
                setState(prevState => ({
                  ...prevState,
                  isDraggingOnDropZone: false,
                }));
              }}
              onDrop={evt => {
                evt.stopPropagation();
                evt.preventDefault();
                if (evt.dataTransfer.files) {
                  evt.dataTransfer.files.length > 1
                  ? toast.error('Please drop only one file.')
                  : handleFileChange({
                    target: evt.dataTransfer
                  })
                }
                setState(prevState => ({
                  ...prevState,
                  isDraggingOnDropZone: false,
                }));
              }}
              onDragOver={evt => {
                evt.stopPropagation();
                evt.preventDefault();
              }}
            >
              <Tooltip
                title={uploadedVoicemail?.name || ""}
                placement="top-start"
              >
                {state.isDraggingOnDropZone ? (
                  <InsertDriveFileIcon fontSize="small" />
                ) : (
                  <Typography variant="body2" noWrap>
                    {uploadedVoicemail?.name
                      ? uploadedVoicemail.name
                      : "Drop a file from your computer here."}
                  </Typography>
                )}
              </Tooltip>
            </Box>
            <Button variant="contained" component="label" htmlFor="audioFile">
              Browse Files
            </Button>
          </Box>
          <Box
            sx={{
              ...globalStyles.display.flex,
              ...globalStyles.flex.justify.end,
            }}
          >
            <Typography variant="body2" sx={{ mr: 2, ...style.uploadNotes }}>
              Max Size: 20MB | 3min
            </Typography>
            <Typography variant="body2" sx={{ ...style.uploadNotes }}>
              File Types: wav and mp3
            </Typography>
          </Box>
          <input
            hidden
            id="audioFile"
            type="file"
            accept={`audio/${SUPPORTED_FILE_FORMATS.join(",")}`}
            onChange={handleFileChange}
            onClick={(evt) => (evt.currentTarget.value = "")}
          />
        </Box>
      )}
      {state.audioSource && hasPermission(+recordingType) && (
        <Box
          sx={{
            mt: 2,
            ...style.audioPlayerWrapper,
          }}
        >
          {callToRecordVoicemail?.contentUri && (
            <Typography
              variant="body2"
              sx={{ mb: 1, ...globalStyles.text.weight.bold }}
            >
              Audio Recieved
            </Typography>
          )}
          <audio controls src={state.audioSource} />
        </Box>
      )}
    </>
  );
};

export default AudioRecordView;
